import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import Card from "../../../core/Card";
import style from "../../../views/style.module.css";
import { apiRequest } from "../../../services/apiService";
import Svg from "../../../component/Svg";

const ENABLE_UAT_SYNC = process.env.REACT_APP_ENABLE_UAT_SYNC === "true";

function ModalPortal({ open, children }) {
  if (!open || typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

function ModalBackdrop({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="modal-backdrop fade show"
      onClick={onClose}
      aria-hidden="true"
    />
  );
}

export function RoiUatSyncToolbarButton({ canAdd, userId, onSynced }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uatList, setUatList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  if (!ENABLE_UAT_SYNC || !canAdd) return null;

  const reset = () => {
    setOpen(false);
    setLoading(false);
    setSyncing(false);
    setUatList([]);
    setSelectedId(null);
  };

  const openModal = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { response, error } = await apiRequest({
        method: "GET",
        url: "/roi-sync/getSecondaryRoiList?limit=200&offset=0",
      });
      if (response?.data?.statusCode === 200) {
        setUatList(response?.data?.data || []);
      } else {
        toast.error(
          error?.response?.data?.msg || "Failed to load UAT calculators"
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to load UAT calculators");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!selectedId) {
      toast.error("Please select a calculator to sync");
      return;
    }
    setSyncing(true);
    try {
      const { response, error } = await apiRequest({
        method: "POST",
        url: "/roi-sync/syncRoiFromUAT",
        data: { roi_id: selectedId, user_id: userId },
      });
      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.msg || "Calculator synced successfully");
        reset();
        onSynced?.();
      } else {
        toast.error(error?.response?.data?.msg || "Failed to sync calculator");
        setSyncing(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to sync calculator");
      setSyncing(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-bordered add-btn ms-md-2 mt-2 mt-md-0"
        onClick={openModal}
      >
        Sync from UAT
      </button>

      <ModalPortal open={open}>
        <div
          className="customvar-modal modal custom-modal fade deleteModal show"
          style={{ display: "block" }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-body">
                <div className={`formcontainer ${style.container}`}>
                  <div
                    className={`formvawrapper delete-modal-wrap modal-setting-medium ${style.wrapper}`}
                  >
                    <Card view="raised" className={`formstyle p-0 ${style.card}`}>
                      <div className="delete-body-wrap delete-language p-4">
                        <div className="heading mb-3">
                          <h3>Sync from UAT</h3>
                          <p>
                            Select a calculator from UAT to import into this
                            environment.
                          </p>
                        </div>
                        {loading ? (
                          <p>Loading UAT calculators...</p>
                        ) : (
                          <div
                            className="table-responsive"
                            style={{ maxHeight: 320, overflow: "auto" }}
                          >
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th style={{ width: 40 }}></th>
                                  <th>Name</th>
                                  <th>Status</th>
                                  <th>Language</th>
                                </tr>
                              </thead>
                              <tbody>
                                {uatList.length === 0 ? (
                                  <tr>
                                    <td colSpan={4}>No calculators found in UAT</td>
                                  </tr>
                                ) : (
                                  uatList.map((item) => (
                                    <tr key={item.roi_id}>
                                      <td>
                                        <input
                                          type="radio"
                                          name="uatRoi"
                                          checked={selectedId === item.roi_id}
                                          onChange={() =>
                                            setSelectedId(item.roi_id)
                                          }
                                        />
                                      </td>
                                      <td>{item.roi_name}</td>
                                      <td>{item.status}</td>
                                      <td>{item.default_language}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <div className="button-wrap d-flex justify-content-end mt-3">
                          <button
                            type="button"
                            className="btn btn-bordered color-cancel"
                            onClick={reset}
                            disabled={syncing}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary ms-3"
                            onClick={handleSync}
                            disabled={syncing || !selectedId}
                          >
                            {syncing ? "Syncing..." : "Sync"}
                          </button>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ModalBackdrop open={open} onClose={reset} />
      </ModalPortal>
    </>
  );
}

export function RoiUatRowSyncMenuItems({ canEdit, onSyncFromUat, onVersionHistory }) {
  if (!ENABLE_UAT_SYNC || !canEdit) return null;

  return (
    <>
      <li>
        <button
          className="dropdown-item d-flex align-items-center"
          type="button"
          onClick={onSyncFromUat}
        >
          <Svg name="dropdown-staging" />
          <span className="dropdown-item ms-2">Sync from UAT</span>
        </button>
      </li>
      <li>
        <button
          className="dropdown-item d-flex align-items-center"
          type="button"
          onClick={onVersionHistory}
        >
          <Svg name="dropdown-version-history" />
          <span className="dropdown-item ms-2">Version history</span>
        </button>
      </li>
    </>
  );
}

export function RoiUatRowSyncModals({
  row,
  activeModal,
  onClose,
  userId,
  onSynced,
}) {
  const [syncMode, setSyncMode] = useState("full");
  const [isSyncing, setIsSyncing] = useState(false);

  const [mergePreviewOpen, setMergePreviewOpen] = useState(false);
  const [mergePreviewData, setMergePreviewData] = useState(null);
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false);
  const [mergeExecuting, setMergeExecuting] = useState(false);

  const [versionRows, setVersionRows] = useState([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const roiId = row?.id;
  const syncOptionsOpen = activeModal === "options";
  const versionOpen = activeModal === "version";

  useEffect(() => {
    if (!versionOpen || !roiId) return;

    let cancelled = false;
    const loadVersions = async () => {
      setVersionLoading(true);
      setVersionRows([]);
      try {
        const { response, error } = await apiRequest({
          method: "GET",
          url: `/roi-sync/getVersionHistory?roi_id=${roiId}&limit=5`,
        });
        if (!cancelled && response?.data?.statusCode === 200) {
          setVersionRows(response?.data?.data || []);
        } else if (!cancelled) {
          toast.error(
            error?.response?.data?.msg || "Failed to load version history"
          );
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(
            error?.response?.data?.msg || "Failed to load version history"
          );
        }
      } finally {
        if (!cancelled) setVersionLoading(false);
      }
    };

    loadVersions();
    return () => {
      cancelled = true;
    };
  }, [versionOpen, roiId]);

  useEffect(() => {
    if (syncOptionsOpen) {
      setSyncMode("full");
      setMergePreviewOpen(false);
      setMergePreviewData(null);
    }
  }, [syncOptionsOpen, roiId]);

  const closeAll = () => {
    setMergePreviewOpen(false);
    setMergePreviewData(null);
    onClose?.();
  };

  const handleFullReplace = async () => {
    setIsSyncing(true);
    try {
      const { response, error } = await apiRequest({
        method: "POST",
        url: "/roi-sync/fullReplaceRoiFromUAT",
        data: { roi_id: roiId, user_id: userId },
      });
      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.msg || "Calculator replaced from UAT");
        closeAll();
        onSynced?.();
      } else {
        toast.error(error?.response?.data?.msg || "Full replacement failed");
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Full replacement failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const handlePreviewMerge = async () => {
    setMergePreviewLoading(true);
    try {
      const { response, error } = await apiRequest({
        method: "POST",
        url: "/roi-sync/previewMerge",
        data: { roi_id: roiId },
      });
      if (response?.data?.statusCode === 200) {
        setMergePreviewData(response?.data?.data);
        setMergePreviewOpen(true);
      } else {
        toast.error(error?.response?.data?.msg || "Merge preview failed");
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Merge preview failed");
    } finally {
      setMergePreviewLoading(false);
    }
  };

  const handleExecuteMerge = async () => {
    setMergeExecuting(true);
    try {
      const { response, error } = await apiRequest({
        method: "POST",
        url: "/roi-sync/executeMerge",
        data: { roi_id: roiId, user_id: userId, user_approved: true },
      });
      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.msg || "Merge completed");
        setMergePreviewOpen(false);
        setMergePreviewData(null);
        closeAll();
        onSynced?.();
      } else {
        toast.error(error?.response?.data?.msg || "Merge failed");
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Merge failed");
    } finally {
      setMergeExecuting(false);
    }
  };

  const handleRestore = async (version) => {
    setRestoringId(version.version_id);
    try {
      const { response, error } = await apiRequest({
        method: "POST",
        url: "/roi-sync/restoreRoiVersion",
        data: { version_id: version.version_id, user_id: userId },
      });
      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.msg || "Version restored");
        closeAll();
        onSynced?.();
      } else {
        toast.error(error?.response?.data?.msg || "Restore failed");
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Restore failed");
    } finally {
      setRestoringId(null);
    }
  };

  const summary = mergePreviewData?.summary;
  const anyOpen = syncOptionsOpen || versionOpen || mergePreviewOpen;

  if (!row || !anyOpen) return null;

  return (
    <ModalPortal open={anyOpen}>
      {syncOptionsOpen && !mergePreviewOpen && (
        <div
          className="customvar-modal modal custom-modal fade deleteModal show"
          style={{ display: "block" }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <div className={`formcontainer ${style.container}`}>
                  <div
                    className={`formvawrapper delete-modal-wrap modal-setting-medium ${style.wrapper}`}
                  >
                    <Card view="raised" className={`formstyle p-0 ${style.card}`}>
                      <div className="delete-body-wrap delete-language p-4">
                        <div className="heading mb-3">
                          <h3>Sync from UAT</h3>
                          <p className="mb-0">
                            Calculator: <strong>{row?.name}</strong>
                          </p>
                        </div>
                        <div className="mb-3">
                          <label className="d-flex align-items-start gap-2 mb-3">
                            <input
                              type="radio"
                              name={`syncMode-${roiId}`}
                              checked={syncMode === "full"}
                              onChange={() => setSyncMode("full")}
                              className="mt-1"
                            />
                            <span>
                              Full replacement
                              <br />
                              <small className="text-muted">
                                UAT overwrites the primary calculator content
                              </small>
                            </span>
                          </label>
                          <label className="d-flex align-items-start gap-2">
                            <input
                              type="radio"
                              name={`syncMode-${roiId}`}
                              checked={syncMode === "merge"}
                              onChange={() => setSyncMode("merge")}
                              className="mt-1"
                            />
                            <span>
                              Merge
                              <br />
                              <small className="text-muted">
                                Preview changes before applying
                              </small>
                            </span>
                          </label>
                        </div>
                        <div className="button-wrap d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-bordered color-cancel"
                            onClick={closeAll}
                            disabled={isSyncing || mergePreviewLoading}
                          >
                            Cancel
                          </button>
                          {syncMode === "full" ? (
                            <button
                              type="button"
                              className="btn btn-primary ms-3"
                              onClick={handleFullReplace}
                              disabled={isSyncing}
                            >
                              {isSyncing ? "Syncing..." : "Sync"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-primary ms-3"
                              onClick={handlePreviewMerge}
                              disabled={mergePreviewLoading}
                            >
                              {mergePreviewLoading ? "Loading..." : "Next"}
                            </button>
                          )}
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {mergePreviewOpen && (
        <div
          className="customvar-modal modal custom-modal fade deleteModal show"
          style={{ display: "block" }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-body">
                <div className={`formcontainer ${style.container}`}>
                  <div
                    className={`formvawrapper delete-modal-wrap modal-setting-medium ${style.wrapper}`}
                  >
                    <Card view="raised" className={`formstyle p-0 ${style.card}`}>
                      <div className="delete-body-wrap delete-language p-4">
                        <div className="heading mb-3">
                          <h3>Merge preview</h3>
                          <p className="mb-0">
                            Calculator: <strong>{row?.name}</strong>
                          </p>
                        </div>
                        {summary && (
                          <ul className="mb-3">
                            <li>New items: {summary.total_new}</li>
                            <li>Conflicts (UAT wins): {summary.total_conflicts}</li>
                            <li>Unchanged: {summary.total_unchanged}</li>
                            <li>Duplicates (both kept): {summary.total_duplicates}</li>
                            <li>Primary only (kept): {summary.total_primary_only}</li>
                          </ul>
                        )}
                        <div className="button-wrap d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-bordered color-cancel"
                            onClick={() => {
                              setMergePreviewOpen(false);
                              setMergePreviewData(null);
                            }}
                            disabled={mergeExecuting}
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary ms-3"
                            onClick={handleExecuteMerge}
                            disabled={mergeExecuting}
                          >
                            {mergeExecuting ? "Merging..." : "Accept & Sync"}
                          </button>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {versionOpen && (
        <div
          className="customvar-modal modal custom-modal fade deleteModal show"
          style={{ display: "block" }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-body">
                <div className={`formcontainer ${style.container}`}>
                  <div
                    className={`formvawrapper delete-modal-wrap modal-setting-medium ${style.wrapper}`}
                  >
                    <Card view="raised" className={`formstyle p-0 ${style.card}`}>
                      <div className="delete-body-wrap delete-language p-4">
                        <div className="heading mb-3">
                          <h3>
                            Version history
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#9ca3af",
                                marginLeft: "12px",
                              }}
                            >
                              Show only latest 5 versions
                            </span>
                          </h3>
                          <p className="mb-0">
                            Calculator: <strong>{row?.name}</strong>
                          </p>
                        </div>
                        <div
                          className="table-responsive mb-3"
                          style={{ maxHeight: 360, overflow: "auto" }}
                        >
                          <table className="table table-sm table-hover">
                            <thead>
                              <tr>
                                <th>Version</th>
                                <th>Label</th>
                                <th>Date</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {versionLoading ? (
                                <tr>
                                  <td colSpan={4}>Loading versions...</td>
                                </tr>
                              ) : versionRows.length === 0 ? (
                                <tr>
                                  <td colSpan={4}>No versions found</td>
                                </tr>
                              ) : (
                                versionRows.map((v) => (
                                  <tr key={v.version_id}>
                                    <td>{v.version_number}</td>
                                    <td>{v.version_label}</td>
                                    <td>{v.created_at}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        disabled={restoringId === v.version_id}
                                        onClick={() => handleRestore(v)}
                                      >
                                        {restoringId === v.version_id
                                          ? "Restoring..."
                                          : "Restore"}
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="button-wrap d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={closeAll}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ModalBackdrop
        open={anyOpen}
        onClose={() => {
          if (!isSyncing && !mergeExecuting && !mergePreviewLoading) {
            closeAll();
          }
        }}
      />
    </ModalPortal>
  );
}
